'use client'

import React, { forwardRef, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/utils'
import { colors } from '../tokens/colors'
import { LucideIcon, X } from 'lucide-react'

// Tipos base do modal
type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

// Interface para aparência do modal
interface ModalAppearance {
  backgroundColor?: string
  textColor?: string
  overlayColor?: string
}

interface ModalProps {
  // Estado do modal
  open: boolean
  onClose?: () => void
  
  // Conteúdo
  title?: string
  children: React.ReactNode
  
  // Configuração visual
  size?: ModalSize
  
  // Header
  showCloseButton?: boolean
  headerIcon?: LucideIcon
  
  // Customização
  appearance?: ModalAppearance
  className?: string
  overlayClassName?: string
  
  // Comportamento
  closeOnOverlayClick?: boolean
  closeOnEscape?: boolean
  preventBodyScroll?: boolean
  
  // Acessibilidade
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

// Preset de aparência padrão
const defaultAppearance: ModalAppearance = {
  backgroundColor: colors.background.white,
  textColor: colors.text.primary,
  overlayColor: 'rgba(0, 0, 0, 0.5)',
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>((
  {
    open,
    onClose,
    title,
    children,
    size = 'md',
    showCloseButton = true,
    headerIcon: HeaderIcon,
    appearance,
    className,
    overlayClassName,
    closeOnOverlayClick = true,
    closeOnEscape = true,
    preventBodyScroll = true,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
    ...props
  },
  ref
) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Determinar aparência final
  const finalAppearance: ModalAppearance = {
    backgroundColor: appearance?.backgroundColor || defaultAppearance.backgroundColor,
    textColor: appearance?.textColor || defaultAppearance.textColor,
    overlayColor: appearance?.overlayColor || defaultAppearance.overlayColor,
  }

  // Classes de tamanho
  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full mx-4',
  }

  // Efeito para gerenciar o foco e scroll do body
  useEffect(() => {
    if (open) {
      // Salvar o elemento focado anteriormente
      previousFocusRef.current = document.activeElement as HTMLElement
      
      // Prevenir scroll do body se necessário
      if (preventBodyScroll) {
        document.body.style.overflow = 'hidden'
      }
      
      // Focar no modal
      setTimeout(() => {
        modalRef.current?.focus()
      }, 100)
    }

    return () => {
      // Restaurar scroll do body
      if (preventBodyScroll) {
        document.body.style.overflow = ''
      }
      
      // Restaurar foco anterior
      if (previousFocusRef.current && open) {
        previousFocusRef.current.focus()
      }
    }
  }, [open, preventBodyScroll])

  // Handler de tecla ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape && onClose) {
        onClose()
      }
    }

    if (open) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [open, closeOnEscape, onClose])

  // Handler do clique no overlay
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnOverlayClick && onClose) {
      onClose()
    }
  }

  // Handler do botão fechar
  const handleCloseClick = () => {
    onClose?.()
  }

  // Se modal não está aberto, não renderizar
  if (!open) {
    return null
  }

  // Classes do overlay
  const overlayClasses = cn(
    'fixed inset-0 z-50 flex items-center justify-center p-4',
    'animate-in fade-in-0 duration-300',
    overlayClassName
  )

  // Classes do modal
  const modalClasses = cn(
    'relative flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden rounded-lg shadow-2xl',
    'animate-in zoom-in-95 slide-in-from-bottom-2 fade-in-0 duration-300',
    sizes[size],
    className
  )

  // Estilos do overlay
  const overlayStyles: React.CSSProperties = {
    backgroundColor: finalAppearance.overlayColor,
  }

  // Estilos do modal
  const modalStyles: React.CSSProperties = {
    backgroundColor: finalAppearance.backgroundColor,
    color: finalAppearance.textColor,
  }

  // Renderizar via portal
  const modalContent = (
    <div
      className={overlayClasses}
      style={overlayStyles}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      aria-describedby={ariaDescribedby}
    >
      <div
        {...props}
        ref={ref || modalRef}
        className={modalClasses}
        style={modalStyles}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between p-6 border-b border-border-primary">
            <div className="flex items-center gap-3">
              {HeaderIcon && (
                <HeaderIcon className="w-5 h-5" style={{ color: finalAppearance.textColor }} />
              )}
              {title && (
                <h2 className="text-lg font-semibold" style={{ color: finalAppearance.textColor }}>
                  {title}
                </h2>
              )}
            </div>
            
            {showCloseButton && (
              <button
                type="button"
                onClick={handleCloseClick}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Fechar modal"
              >
                <X className="w-5 h-5" style={{ color: finalAppearance.textColor }} />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-6">
          {children}
        </div>
      </div>
    </div>
  )

  // Renderizar no portal se possível, senão renderizar normalmente
  if (typeof window !== 'undefined') {
    return createPortal(modalContent, document.body)
  }

  return modalContent
})

Modal.displayName = 'Modal'
